/**
 * Ctrl+Shift+P command palette — searches every ribbon and Help command.
 */

import * as React from "react";

import { DialogOverlay, DialogPanel } from "@/components/ui/dialog";
import { dispatchAction } from "@/shell/actions";
import { searchCommands, type CommandEntry } from "@/shell/commands";
import { useRibbonContext } from "@/store/appStore";
import { cn } from "@/lib/utils";

let openPalette: (() => void) | null = null;

export function requestCommandPalette(): void {
  openPalette?.();
}

export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [index, setIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const ctx = useRibbonContext();

  React.useEffect(() => {
    openPalette = () => {
      setQuery("");
      setIndex(0);
      setOpen(true);
    };
    return () => {
      if (openPalette) openPalette = null;
    };
  }, []);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "p") {
        event.preventDefault();
        setQuery("");
        setIndex(0);
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  React.useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const hits = React.useMemo(() => searchCommands(query), [query]);
  const active = hits[index] ?? null;

  const run = (entry: CommandEntry) => {
    if (entry.disabled?.(ctx)) return;
    dispatchAction(entry.action, { itemId: entry.id });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <DialogOverlay align="top" className="z-[80] pt-[12vh]" onMouseDown={() => setOpen(false)}>
      <DialogPanel className="w-[520px]" onMouseDown={(event) => event.stopPropagation()}>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
              return;
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setIndex((i) => Math.min(i + 1, Math.max(hits.length - 1, 0)));
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setIndex((i) => Math.max(i - 1, 0));
              return;
            }
            if (event.key === "Enter" && active) run(active);
          }}
          placeholder="Search commands…"
          className="h-12 w-full border-b border-black/[0.06] bg-transparent px-5 text-[15px] outline-none placeholder:text-ads-faint"
        />
        <div className="max-h-80 overflow-auto p-1.5">
          {hits.length === 0 ? (
            <p className="px-3 py-4 text-center text-[12px] text-ads-muted">No matching commands.</p>
          ) : (
            hits.map((entry, i) => {
              const disabled = entry.disabled?.(ctx) ?? false;
              return (
                <button
                  key={entry.id}
                  type="button"
                  disabled={disabled}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => run(entry)}
                  className={cn(
                    "flex w-full flex-col rounded-xl px-3 py-1.5 text-left transition-colors",
                    disabled ? "text-ads-faint" : "text-ads-text",
                    i === index ? "bg-ads-accent-soft" : "hover:bg-black/[0.03]",
                  )}
                >
                  <span className="text-[12.5px]">{entry.label}</span>
                  <span className="text-[10.5px] text-ads-muted">{entry.path}</span>
                </button>
              );
            })
          )}
        </div>
        <div className="border-t border-black/[0.06] bg-neutral-50/60 px-4 py-2 text-[10.5px] text-ads-muted">
          Ctrl+Shift+P · Enter to run · Esc to close
        </div>
      </DialogPanel>
    </DialogOverlay>
  );
}
